import React, { useEffect, useState } from "react";
import axios from "axios";
import { cachedGet } from "./apiCache.js";
import { useLocation, useNavigate } from "react-router-dom";
import { useDateFilter } from "./_40b_date_filter_context.js";
import { useModuleVisibility, getActiveModule } from "./useModuleVisibility.js";
import { FullscreenIcon, getFullscreenContainerStyles, getFullscreenHeaderStyles, getFullscreenContentStyles, useFullscreenAttribute } from "./fullscreenComponentUtils.js";

const MODULE_NAME = "_4_today_trade_table";

function Tooltip({ text }) {
    return (
        <span
            style={{
                marginLeft: 4,
                verticalAlign: "middle",
                cursor: "pointer",
                color: "#888"
            }}
            title={text}
        >
            🛈
        </span>
    );
}

function formatHeadlineDate(dateStr) {
    if (!dateStr) return "";
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [year, month, day] = [isoMatch[1], isoMatch[2], isoMatch[3]];
        const dateObj = new Date(`${year}-${month}-${day}T00:00:00`);
        if (!isNaN(dateObj)) {
            const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" });
            const monthAbbr = dateObj.toLocaleDateString("en-US", { month: "short" });
            const dayNum = dateObj.getDate();
            return `${weekday} ${monthAbbr} ${dayNum}`;
        }
    }
    const dateObj = new Date(dateStr);
    if (!isNaN(dateObj)) {
        const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" });
        const monthAbbr = dateObj.toLocaleDateString("en-US", { month: "short" });
        const dayNum = dateObj.getDate();
        return `${weekday} ${monthAbbr} ${dayNum}`;
    }
    return dateStr;
}

function colorPL(val) {
    if (val == null) return '';
    return typeof val === 'number'
        ? val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : val;
}

function getPLColor(val) {
    if (val == null || isNaN(val)) return "#fff";
    if (val > 0) return "#4ade80";
    if (val < 0) return "#f87171";
    return "#fff";
}

function formatK(amount) {
    if (amount == null || isNaN(amount)) return "";
    if (Math.abs(amount) >= 1000000) {
        return (amount / 1000000).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "M";
    }
    if (Math.abs(amount) >= 1000) {
        return (amount / 1000).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "K";
    }
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatQty(qty) {
    if (qty == null || isNaN(qty)) return "";
    return Math.floor(qty).toLocaleString();
}

const initialColumns = [
    { key: 'Symbol', label: 'Instrument' },
    { key: 'Symbol_PL_today_raw', label: 'Profit/Loss' },
    { key: 'Qty_buy', label: 'Qty' },
    { key: 'Trades', label: 'Trades' },
    { key: 'PL_per_Trades', label: 'P/L per Trade' },
    { key: 'Lowest_Price', label: 'Lowest price' },
    { key: 'Largest_Intra_Day_Cash', label: 'Largest Intra Day Cash' },
    { key: 'Risk_Percentage', label: 'Risk%' },
    { key: 'Max_Intra_Day_Qty', label: 'Max Intra Day Qty' },
    { key: 'Pos_Effect', label: 'Pos Effect' }
];

function MultiDayBadge() {
    return (
        <span
            style={{
                display: "inline-block",
                marginLeft: 6,
                padding: "2px 6px",
                backgroundColor: "#7c3aed",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 3,
                whiteSpace: "nowrap"
            }}
            title="Position carried from previous day (no matching TO OPEN on current day)"
        >
            Multi-Day
        </span>
    );
}

function TodayTradeTable({ activeDate, latest_date, refreshKey }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { navigationDate } = useDateFilter();

    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [collapsed, setCollapsed] = useState(false);
    const [columns, setColumns] = useState(initialColumns);
    const [draggedColIdx, setDraggedColIdx] = useState(null);

    const isVisible = useModuleVisibility(MODULE_NAME);
    const isFullscreen = getActiveModule() === MODULE_NAME;

    // Use centralized hook for fullscreen attribute
    useFullscreenAttribute(isFullscreen);

    const effectiveDate =
        (navigationDate && /^\d{4}-\d{2}-\d{2}$/.test(navigationDate) ? navigationDate : null) ||
        (activeDate && /^\d{4}-\d{2}-\d{2}$/.test(activeDate) ? activeDate : null) ||
        (latest_date && /^\d{4}-\d{2}-\d{2}$/.test(latest_date) ? latest_date : null) ||
        (sessionStorage.getItem("latest_date") && /^\d{4}-\d{2}-\d{2}$/.test(sessionStorage.getItem("latest_date")) ? sessionStorage.getItem("latest_date") : null) ||
        new Date().toISOString().slice(0, 10);

    useEffect(() => {
        setError("");
        if (!effectiveDate) {
            setError("No date available for trade data.");
            return;
        }

        const userKey = sessionStorage.getItem("userKey");
        const fetchParams = { 
            date: effectiveDate, 
            refreshKey 
        };

        if (userKey) {
            fetchParams.userKey = userKey;
        }

        cachedGet(
            `${process.env.REACT_APP_API_URL}/api/today-trade-table`,
            fetchParams,
            { withCredentials: true }
        )
            .then(res => {
                setData(res.data);
            })
            .catch(() => setError("Could not fetch trade data"));
    }, [effectiveDate, refreshKey]);

    const displayError = error || (data && data.error);
    if (!data && !error) return <div>Loading...</div>;

    const summaryList = data?.summary || [];
    const sortedSummary = summaryList
        .filter(row => row.Symbol !== "🧮 Total")
        .sort((a, b) => (b.Symbol_PL_today_raw || 0) - (a.Symbol_PL_today_raw || 0));

    const totalRow = summaryList.find(row => row.Symbol === "🧮 Total");

    const onDragStart = (idx) => setDraggedColIdx(idx);

    const onDragOver = (e) => e.preventDefault();

    const onDrop = (idx) => {
        if (draggedColIdx === null || draggedColIdx === idx) return;
        const newColumns = [...columns];
        const [removed] = newColumns.splice(draggedColIdx, 1);
        newColumns.splice(idx, 0, removed);
        setColumns(newColumns);
        setDraggedColIdx(null);
    };

    const formatCellValue = (key, val, row) => {
        if (key === 'Symbol') {
            return (
                <span>
                    {val}
                    {row && row.isMulti_daytrade && <MultiDayBadge />}
                </span>
            );
        }
        if (key === 'Largest_Intra_Day_Cash') {
            return formatK(val);
        }
        if (key === 'Qty_buy' || key === 'Max_Intra_Day_Qty') {
            return formatQty(val);
        }
        if (key === 'Symbol_PL_today_raw') {
            return colorPL(val);
        }
        if (key === 'Lowest_Price') {
            return val != null ? val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        }
        return val;
    };

    const getCellColor = (key, val) => {
        if (key === 'Symbol_PL_today_raw') {
            return getPLColor(val);
        }
        return "#fff";
    };

    // Return null if component is not visible due to module_name filter
    if (!isVisible) return null;

    return (
        <div 
            className={`collapse-toggle-container${collapsed ? " collapsed" : ""} today-trade-table-container`}
            style={getFullscreenContainerStyles(isFullscreen)}
            data-analytics={MODULE_NAME}
        >
            <div style={getFullscreenHeaderStyles(isFullscreen)}>
                <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <h4 className="collapse-toggle-headline" onClick={() => setCollapsed(!collapsed)} style={{ cursor: "pointer", margin: 0 }}>
                        🔍 Equities traded – {formatHeadlineDate(data?.date || effectiveDate)}
                        <Tooltip text={data?.tooltip} />
                    </h4>
                </div>
                {!isFullscreen && (
                    <FullscreenIcon 
                        moduleName={MODULE_NAME}
                        title="Equities Traded Table"
                        additionalParams={{ date: data?.date || effectiveDate }}
                    />
                )}
            </div>

            <div className="collapse-content" style={getFullscreenContentStyles(isFullscreen)}>
                {displayError && (
                    <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 4, color: "#ffbaba", backgroundColor: "#3a1e1e", fontSize: 13, border: "1px solid #e63946" }}>
                        {displayError}
                    </div>
                )}

                {!displayError && sortedSummary.length > 0 && (
                    <div style={{ overflowX: "auto", width: "100%" }}>
                        <table className="today-trade-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                                <tr>
                                    {columns.map((col, idx) =>
                                        <th
                                            key={col.key}
                                            style={{ padding: "8px 6px", fontSize: 12, textAlign: "left", borderBottom: "1px solid #eee", fontWeight: 600, whiteSpace: "nowrap" }}
                                            draggable
                                            onDragStart={() => onDragStart(idx)}
                                            onDragOver={onDragOver}
                                            onDrop={() => onDrop(idx)}
                                        >
                                            {col.label}
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSummary.map((row, i) => (
                                    <tr key={i} style={{ borderBottom: "1px solid #333", background: i % 2 === 0 ? "transparent" : "#1a1d25" }}>
                                        {columns.map((col) => (
                                            <td key={col.key} style={{ padding: "6px 6px", fontWeight: "bold", color: getCellColor(col.key, row[col.key]), whiteSpace: "nowrap" }}>
                                                {formatCellValue(col.key, row[col.key], row)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {totalRow && (
                                    <tr style={{ fontWeight: "bold", background: "#444", borderBottom: "2px solid #666" }}>
                                        {columns.map((col) => (
                                            <td key={col.key} style={{ padding: "8px 6px", color: getCellColor(col.key, totalRow[col.key]), whiteSpace: "nowrap" }}>
                                                {formatCellValue(col.key, totalRow[col.key], totalRow)}
                                            </td>
                                        ))}
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {!displayError && sortedSummary.length === 0 && (
                    <div style={{ marginTop: 12, color: "#999", fontSize: 13 }}>No trades found for this date.</div>
                )}
            </div>
        </div>
    );
}

export default TodayTradeTable;